import type { TagManager } from './TagManager/TagManager.types';
import type { TelemetryEvent, TelemetryEventName } from './TelemetryEvent';
import type {
  TelemetryBag,
  TelemetrySelectedProduct,
} from './TelemetryEvent.types';
import type { TelemetryEventOverride } from './TelemetryEventOverride';

export const tagManagerEventOverrides: TagManagerEventOverrides = {
  'sign-in.success': ({ context }) => ({
    event: {
      event: 'sign_in',
      userId: context.userId,
    },
  }),
  'join.success': ({ context }) => ({
    event: {
      event: 'registration_completed',
      userId: context.userId,
    },
  }),
  'menu.view': ({ context, event: { payload } }) => ({
    event: {
      event: 'view_restaurant',
      userId: context.userId,
      restaurant_id: payload.restaurant?.id,
    },
  }),
  'menu.quick_add_to_bag': ({ context, event: { payload } }) => ({
    event: {
      event: 'view_product',
      userId: context.userId,
      ...formatSelectedProduct(payload.selectedProduct),
    },
  }),
  'pdp.add_to_bag': ({ context, event: { payload } }) => ({
    event: {
      event: 'add_to_cart',
      userId: context.userId,
      ...formatSelectedProduct(payload.selectedProduct),
      ...formatBagWithSelectedProduct(payload),
      ...formatCustomerPersonalInfo(payload),
    },
  }),
  'pdp.view': ({ context, event: { payload } }) => ({
    event: {
      event: 'view_product',
      userId: context.userId,
      ...formatSelectedProduct(payload.selectedProduct),
    },
  }),
  'checkout.view': ({ context, event: { payload } }) => ({
    event: {
      event: 'initiate_checkout',
      userId: context.userId,
      ...formatBag(payload),
      ...formatCustomerPersonalInfo(payload),
    },
  }),
  'checkout.success': ({ context, event: { payload } }) => ({
    event: {
      event: 'purchase',
      userId: context.userId,
      ...formatBag(payload),
      ...formatCustomerPersonalInfo(payload),
    },
  }),
};

// –– HELPERS –––––––––––––––––––––––––

export const formatSelectedProduct = (product?: TelemetrySelectedProduct) => ({
  product_id: product?.id,
  product_name: product?.name,
  product_cost: product?.cost ?? 0,
  quantity: product?.quantity ?? 0,
  restaurant_id: product?.restaurantId,
  product_collection_id: product?.category,
});

export const formatBag = (payload: TelemetryBag) => ({
  master_product_ids: payload.productIds,
  product_names: payload.productNames,
  entity_id: payload.restaurantId,
  order_id: payload.orderId,
  total_value: payload.orderValue ?? 0,
  order_value: payload.orderValue ?? 0,
  order_type: payload.orderType,
  number_of_items: payload.bagCount ?? 0,
});

export const formatBagWithSelectedProduct = (
  payload: TelemetryEvent<'pdp.add_to_bag'>['payload'],
) => ({
  master_product_ids: payload.selectedProduct?.id
    ? [...(payload.productIds ?? []), payload.selectedProduct.id]
    : payload.productIds,
  product_names: payload.selectedProduct?.name
    ? [...(payload.productNames ?? []), payload.selectedProduct.name]
    : payload.productNames,
  entity_id: payload.restaurantId,
  order_id: payload.orderId,
  total_value: (payload.orderValue ?? 0) + (payload.selectedProduct?.cost ?? 0),
  order_value: (payload.orderValue ?? 0) + (payload.selectedProduct?.cost ?? 0),
  order_type: payload.orderType,
  number_of_items:
    (payload.bagCount ?? 0) + (payload.selectedProduct?.quantity ?? 0),
});

export const formatCustomerPersonalInfo = (payload: TelemetryBag) => {
  const info = {
    email: payload.customer?.email,
    phone_number: payload.customer?.phoneNumber,
    first_name: payload.customer?.firstName,
    last_name: payload.customer?.lastName,
    street: payload.street,
    city: payload.city,
    state: payload.state,
    postal_code: payload.zipCode,
    country: payload.country,
  };

  return {
    // enhanced conversion data object version.
    enhanced_conversion_data: info,

    // "dlv_" prefixed version.
    dlv_email: info.email,
    dlv_phone_number: info.phone_number,
    dlv_first_name: info.first_name,
    dlv_last_name: info.last_name,
    dlv_street: info.street,
    dlv_city: info.city,
    dlv_state: info.state,
    dlv_postal_code: info.postal_code,
    dlv_country: info.country,

    // "a" prefixed version.
    aFirstName: info.first_name,
    aLastName: info.last_name,
    aEmail: info.email,
    aPhone: info.phone_number,
    aStreet: info.street,
    aCity: info.city,
    aState: info.state,
    aCountry: info.country,
    aPostalCode: info.postal_code,

    // spread out version.
    ...info,
  };
};

// –– TYPES –––––––––––––––––––––––––––

export type TagManagerEventOverrides = {
  readonly [Name in TelemetryEventName]?: TelemetryEventOverride<
    Name,
    TagManagerEvent
  >;
};

export type TagManagerEvent = Parameters<TagManager['dataLayer']>[0];
